'use client';

import {useAuth} from 'iq-auth';
import {useEffect, useState} from 'react';
import {track} from 'iq-tracking';
import {getActiveSale} from 'iq-sales/api';
import {Countdown} from './countdown';
import {FRONTEND_URL} from 'config';
import type {Sale} from 'iq-sales/types';

const MUTE_KEY = 'mute-sales';

function salesAreMuted(): boolean {
  const muteUntil = localStorage.getItem(MUTE_KEY);
  if (!muteUntil) return false;
  return new Date().toISOString() < muteUntil;
}

function muteSales(days: number) {
  const muteUntil = new Date();
  muteUntil.setDate(muteUntil.getDate() + days);
  localStorage.setItem(MUTE_KEY, muteUntil.toISOString());
}

function ActiveSaleBanner({sale}: {sale: Sale}) {
  const [closed, setClosed] = useState(false);
  const pricingHref = `${FRONTEND_URL}/pricing`;

  useEffect(() => {
    void track({
      event: 'sale_banner',
      action: 'view',
      component: 'banner',
      content_identifier: sale.name,
      extra: {promo_code: sale.stripe_promo_code},
    });
  }, [sale.name]);

  if (closed) return null;

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    muteSales(1);
    setClosed(true);
    void track({
      event: 'sale_banner',
      action: 'close',
      component: 'banner',
      content_identifier: sale.name,
      extra: {promo_code: sale.stripe_promo_code},
    });
  };

  const closeBtn = (
    <button
      onClick={handleClose}
      className='absolute top-1/2 -translate-y-1/2 right-8 flex items-center justify-center p-2 text-white opacity-70 hover:opacity-100 transition-opacity'
      aria-label='Close banner'>
      <svg
        width='20'
        height='20'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'>
        <line x1='18' y1='6' x2='6' y2='18' />
        <line x1='6' y1='6' x2='18' y2='18' />
      </svg>
    </button>
  );

  const content = (
    <div className='flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-8 px-10 py-3 text-white max-w-5xl mx-auto text-center lg:text-left'>
      {/* gift + percent row */}
      <div className='flex items-center justify-center gap-2 mb-2 lg:mb-0'>
        <img
          src='/img/gift.svg'
          alt=''
          width={48}
          height={43}
          className='block lg:hidden shrink-0'
        />
        <img
          src='/img/gift.svg'
          alt=''
          width={80}
          height={72}
          className='hidden lg:block shrink-0 relative -my-3'
        />
        {sale.percent_off && (
          <span className='text-4xl font-bold relative top-[4px]'>{sale.percent_off}% off</span>
        )}
      </div>
      <div>
        <p className='text-base font-bold leading-tight'>{sale.header_text}</p>
        <p className='text-sm opacity-80'>{sale.subheader_text}</p>
      </div>
      <div className='hidden lg:block bg-iq-orange text-white font-bold text-base px-5 py-2 rounded-md min-w-[190px] text-center'>
        <Countdown until={sale.sale_end_at} />
      </div>
    </div>
  );

  return (
    <div className='relative bg-iq-black'>
      <a
        href={pricingHref}
        onClick={() =>
          void track({
            event: 'sale_banner',
            action: 'click',
            component: 'banner',
            content_identifier: sale.name,
            extra: {promo_code: sale.stripe_promo_code},
          })
        }>
        {content}
      </a>
      {closeBtn}
    </div>
  );
}

export function SaleBanner() {
  const {user, isReady} = useAuth();
  const [sale, setSale] = useState<Sale | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    if (user.loggedIn && user.hasSubscription) {
      setLoaded(true);
      return;
    }
    if (salesAreMuted()) {
      setLoaded(true);
      return;
    }
    getActiveSale()
      .then(setSale)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [isReady, user]);

  if (!loaded || !sale) return null;

  return <ActiveSaleBanner sale={sale} />;
}
